import styled, { useTheme } from "styled-components";
import { Info } from "lucide-react";
import Text from "common/Typography/Text";
import { WarningIcon } from "common/Icons/Icons";
import React, { CSSProperties } from "react";
import IntercomLink from "common/Text/IntercomLink";
import { hexToRGB } from "@shared/utils/misc";
import { GROUP_STATISTICS_DISCLAIMER_ARTICLE } from "@shared/features/newMarketplace/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";

interface DisclaimerProps {
  inExtension?: boolean;
  style?: CSSProperties;
}

const Disclaimer = ({ inExtension, style }: DisclaimerProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const { isNewLook } = useTheme();

  if (isNewLook) {
    return (
      <NewLookDisclaimer style={style}>
        <Info size={14} strokeWidth={1.75} aria-hidden={true} />
        <span>
          {t("disclaimer.content")}{" "}
          <NewLookLink articleId={GROUP_STATISTICS_DISCLAIMER_ARTICLE}>
            {t("disclaimer.read_more")}
          </NewLookLink>
        </span>
      </NewLookDisclaimer>
    );
  }

  return (
    <DisclaimerBg style={style} $inExtension={inExtension}>
      <StyledWarningIcon />
      <Text style={{ flex: 1 }}>
        {t("disclaimer.content")}
        <StyledIntercomLink articleId={GROUP_STATISTICS_DISCLAIMER_ARTICLE}>
          {t("disclaimer.read_more")}
        </StyledIntercomLink>
      </Text>
    </DisclaimerBg>
  );
};

export default Disclaimer;

const DisclaimerBg = styled.div<{ $inExtension?: boolean }>`
  background-color: ${(p) =>
    p.$inExtension
      ? hexToRGB(p.theme.colors.secondaryGrey, 0.05)
      : p.theme.colors.statisticsBgColor};
  border-radius: 8px;
  padding: 12px;
  display: flex;
`;
const NewLookDisclaimer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  text-align: left;
  svg {
    flex: none;
    margin-top: 1px;
    color: ${(p) => getNewLookTokens(p.theme).muted};
  }
`;
const NewLookLink = styled(IntercomLink)`
  color: ${(p) => getNewLookTokens(p.theme).accent};
  font-weight: 500;
  white-space: nowrap;
  &:hover {
    color: ${(p) => getNewLookTokens(p.theme).accent};
    text-decoration: underline;
  }
`;
const StyledWarningIcon = styled(WarningIcon)`
  margin-right: 8px;
  width: 20px;
  height: 20px;
`;
const StyledIntercomLink = styled(IntercomLink)<{ $isMobile?: boolean }>`
  color: ${(p) => p.theme.colors.primaryGrey};
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  text-decoration: underline;
  white-space: nowrap;
  font-size: ${(p) =>
    p.$isMobile
      ? p.theme.typography.fontSizes.S
      : p.theme.typography.fontSizes.M};
  margin-left: 8px;
  &:hover {
    color: ${(p) => hexToRGB(p.theme.colors.primaryGrey, 0.85)};
    text-decoration: underline;
  }
`;
